import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea, ScrollBar } from "../components/ui/scroll-area";
import { 
    Ticket, 
    SignOut, 
    Users, 
    Funnel, 
    ArrowClockwise,
    CaretLeft,
    CaretRight,
    Buildings,
    ChatCircle,
    PaperclipHorizontal,
    User,
    CaretUp,
    CaretDown,
    ArrowUp,
    ArrowDown
} from "@phosphor-icons/react";
import { format } from "date-fns";

// Use relative URLs - works on both preview and production since /api is routed by ingress
const API = "";

// Skoda DSCP specific status mapping
const STATUS_MAP = {
    2: { label: "Open", class: "status-open" },
    3: { label: "Pending", class: "status-pending" },
    4: { label: "Resolved", class: "status-resolved" },
    5: { label: "Closed", class: "status-closed" },
    6: { label: "Waiting on Customer", class: "status-pending" },
    9: { label: "Reopened", class: "status-open" },
    10: { label: "Waiting Approval", class: "status-pending" },
    12: { label: "Waiting on Skoda HQ", class: "status-pending" },
    13: { label: "In Analysis", class: "status-open" },
};

// Allowed statuses for filtering
const ALLOWED_STATUSES = [
    { value: 2, label: "Open" },
    { value: 9, label: "Reopened" },
    { value: 13, label: "In Analysis" },
    { value: 10, label: "Waiting Approval" },
    { value: 12, label: "Waiting on Skoda HQ" },
    { value: 6, label: "Waiting on Customer" },
];

// Skoda companies
const SKODA_COMPANY_NAMES = [
    "skoda finland",
    "skoda greece", 
    "skoda hq",
    "skoda ireland",
    "skoda kazakhstan",
    "skoda luxembourg",
    "skoda poland",
    "skoda uk",
    "skoda ukraine"
];

// Priority mapping
const PRIORITY_MAP = {
    1: { label: "Low", class: "priority-low" },
    2: { label: "Medium", class: "priority-medium" },
    3: { label: "High", class: "priority-high" },
    4: { label: "Urgent", class: "priority-urgent" },
};

const DashboardPage = () => {
    const { user, logout } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [allTickets, setAllTickets] = useState([]); // Store all tickets for company extraction
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);

    // Filters
    const [selectedCompany, setSelectedCompany] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");

    // Sorting
    const [sortColumn, setSortColumn] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");

    // Ticket detail modal
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketConversations, setTicketConversations] = useState([]);
    const [loadingConversations, setLoadingConversations] = useState(false);
    
    // Conversation navigation
    const conversationRefs = useRef([]);
    const scrollAreaRef = useRef(null);
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

    // Extract unique companies from tickets
    const extractCompaniesFromTickets = (ticketList) => {
        const companyMap = new Map();
        ticketList.forEach(ticket => {
            if (ticket.company && ticket.company.id && ticket.company.name) {
                if (ticket.company.name.toLowerCase().includes("skoda")) {
                    companyMap.set(ticket.company.id, {
                        id: ticket.company.id,
                        name: ticket.company.name
                    });
                }
            }
        });
        return Array.from(companyMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    };

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams();
            params.append("skoda_only", "true");
            
            if (selectedCompany && selectedCompany !== "all") {
                params.append("company_id", selectedCompany);
            }
            if (selectedStatus && selectedStatus !== "all") {
                params.append("status", selectedStatus);
            }

            const { data } = await axios.get(`${API}/api/freshdesk/tickets?${params.toString()}`, {
                withCredentials: true
            });
            
            let filteredTickets = data.tickets || [];
            
            // Store all Skoda tickets for company extraction (before status filter)
            const allSkodaTickets = filteredTickets.filter(ticket => {
                const companyName = (ticket.company?.name || "").toLowerCase();
                return companyName.includes("skoda");
            });
            
            // Update companies list from all tickets (only on first load or when no company filter)
            if (selectedCompany === "all") {
                const extractedCompanies = extractCompaniesFromTickets(allSkodaTickets);
                if (extractedCompanies.length > 0) {
                    setCompanies(prev => {
                        // Merge with existing to build complete list
                        const merged = new Map();
                        prev.forEach(c => merged.set(c.id, c));
                        extractedCompanies.forEach(c => merged.set(c.id, c));
                        return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
                    });
                }
            }
            
            // Apply filters: Skoda companies only, exclude closed
            filteredTickets = filteredTickets.filter(ticket => {
                const companyName = (ticket.company?.name || "").toLowerCase();
                const isClosed = ticket.status === 5;
                return companyName.includes("skoda") && !isClosed;
            });
            
            setTickets(filteredTickets);
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to fetch tickets");
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }, [page, selectedCompany, selectedStatus]);

    const fetchTicketConversations = useCallback(async (ticketId) => {
        setLoadingConversations(true);
        try {
            const { data } = await axios.get(`${API}/api/freshdesk/tickets/${ticketId}/conversations`, {
                withCredentials: true
            });
            setTicketConversations(data || []);
        } catch (err) {
            console.error("Failed to fetch conversations:", err);
            setTicketConversations([]);
        } finally {
            setLoadingConversations(false);
        }
    }, []);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const handleTicketClick = async (ticket) => {
        setSelectedTicket(ticket);
        setCurrentMessageIndex(0);
        conversationRefs.current = [];
        fetchTicketConversations(ticket.id);
        
        // Fetch full ticket details (includes description_text not in list endpoint)
        try {
            const { data } = await axios.get(`${API}/api/freshdesk/tickets/${ticket.id}`, {
                withCredentials: true
            });
            setSelectedTicket(prev => prev ? { ...prev, description_text: data.description_text } : prev);
        } catch (err) {
            console.error("Failed to fetch ticket details:", err);
        }
    };

    const closeTicketModal = () => {
        setSelectedTicket(null);
        setTicketConversations([]);
        setCurrentMessageIndex(0);
        conversationRefs.current = [];
    };

    // Navigation functions for conversation thread
    const scrollToPreviousMessage = () => {
        if (conversationRefs.current.length === 0) return;
        
        const newIndex = Math.max(0, currentMessageIndex - 1);
        setCurrentMessageIndex(newIndex);
        
        const targetRef = conversationRefs.current[newIndex];
        if (targetRef) {
            targetRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const scrollToNextMessage = () => {
        if (conversationRefs.current.length === 0) return;
        
        const newIndex = Math.min(conversationRefs.current.length - 1, currentMessageIndex + 1);
        setCurrentMessageIndex(newIndex);
        
        const targetRef = conversationRefs.current[newIndex];
        if (targetRef) {
            targetRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleLogout = async () => {
        await logout();
    };

    const clearFilters = () => {
        setSelectedCompany("all");
        setSelectedStatus("all");
        setPage(1);
    };

    // Sorting handler
    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    // Sort tickets
    const sortedTickets = [...tickets].sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];
        
        // Handle nested properties
        if (sortColumn === "company") {
            aVal = a.company?.name || "";
            bVal = b.company?.name || "";
        }
        if (sortColumn === "requester") {
            aVal = a.requester?.email || "";
            bVal = b.requester?.email || "";
        }
        if (sortColumn === "delivery") {
            aVal = a.custom_fields?.cf_deployment_date || "";
            bVal = b.custom_fields?.cf_deployment_date || "";
        }
        if (sortColumn === "type") {
            aVal = a.type || "";
            bVal = b.type || "";
        }
        
        // Handle dates
        if (sortColumn === "created_at" || sortColumn === "updated_at") {
            aVal = new Date(aVal || 0).getTime();
            bVal = new Date(bVal || 0).getTime();
        }
        
        // Handle numbers
        if (typeof aVal === "number" && typeof bVal === "number") {
            return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }
        
        // Handle strings
        aVal = String(aVal || "").toLowerCase();
        bVal = String(bVal || "").toLowerCase();
        
        if (sortDirection === "asc") {
            return aVal.localeCompare(bVal);
        }
        return bVal.localeCompare(aVal);
    });

    const getStatusBadge = (status) => {
        const statusInfo = STATUS_MAP[status] || { label: `Status ${status}`, class: "status-pending" };
        return (
            <span className={`status-badge ${statusInfo.class}`}>
                {statusInfo.label}
            </span>
        );
    };

    const getPriorityBadge = (priority) => {
        const priorityInfo = PRIORITY_MAP[priority] || { label: `P${priority}`, class: "priority-low" };
        return (
            <span className={`status-badge ${priorityInfo.class}`}>
                {priorityInfo.label}
            </span>
        );
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        try {
            return format(new Date(dateStr), "MMM dd, yyyy HH:mm");
        } catch {
            return dateStr;
        }
    };

    const formatDateShort = (dateStr) => {
        if (!dateStr) return "-";
        try {
            return format(new Date(dateStr), "MMM dd, yy");
        } catch {
            return dateStr;
        }
    };

    const formatConversationText = (text) => {
        if (!text) return "";
        // Preserve line breaks and convert common patterns
        return text
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/\\n/g, '\n')
            .replace(/\u00a0/g, ' ') // Non-breaking space
            .replace(/\u200b/g, ''); // Zero-width space
    };

    const stripHtml = (html) => {
        if (!html) return "";
        // Create a temporary element to parse HTML
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Get text content and preserve structure
        let text = "";
        const walk = (node) => {
            if (node.nodeType === 3) { // Text node
                text += node.textContent;
            } else if (node.nodeType === 1) { // Element node
                const tag = node.tagName.toLowerCase();
                // Add line break before block elements
                if (['br', 'p', 'div', 'li', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) {
                    if (text.length > 0 && !text.endsWith('\n')) {
                        text += '\n';
                    }
                }
                for (const child of node.childNodes) {
                    walk(child);
                }
                // Add line break after block elements
                if (['p', 'div', 'li', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) {
                    if (!text.endsWith('\n')) {
                        text += '\n';
                    }
                }
            }
        };
        walk(doc.body);
        // Clean up: replace multiple newlines with double, and trim
        return text
            .replace(/\u00a0/g, ' ') // Non-breaking space
            .replace(/\u200b/g, '') // Zero-width space
            .replace(/[ \t]+\n/g, '\n') // Trailing spaces
            .replace(/\n[ \t]+/g, '\n') // Leading spaces after newline
            .replace(/\n{3,}/g, '\n\n') // Multiple newlines
            .trim();
    };

    const SortableHeader = ({ column, children, className = "" }) => (
        <TableHead 
            className={`text-xs font-bold uppercase tracking-widest py-3 px-2 md:px-4 cursor-pointer hover:bg-[#E4E4E7] select-none ${className}`}
            style={{ fontFamily: 'Chivo, sans-serif' }}
            onClick={() => handleSort(column)}
        >
            <div className="flex items-center gap-1">
                {children}
                {sortColumn === column && (
                    sortDirection === "asc" 
                        ? <CaretUp weight="bold" className="w-3 h-3" />
                        : <CaretDown weight="bold" className="w-3 h-3" />
                )}
            </div>
        </TableHead>
    );

    // Check if ticket is resolved (for gray styling)
    const isResolved = (status) => status === 4;

    return (
        <div className="dashboard-layout">
            {/* Top Navigation */}
            <header className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-border bg-white">
                <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
                        <Ticket weight="bold" className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-base md:text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        OPEN TASKS
                    </span>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    {user?.role === "admin" && (
                        <Link to="/users">
                            <Button 
                                variant="outline" 
                                data-testid="nav-users-button"
                                className="h-9 border-2 hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors duration-100 hidden md:flex"
                            >
                                <Users weight="bold" className="w-4 h-4 mr-2" />
                                Manage Users
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm"
                                data-testid="nav-users-button-mobile"
                                className="h-9 border-2 md:hidden"
                            >
                                <Users weight="bold" className="w-4 h-4" />
                            </Button>
                        </Link>
                    )}
                    <div className="text-sm text-muted-foreground font-medium hidden md:block">
                        {user?.email}
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="h-9 border-2 hover:bg-destructive hover:text-white hover:border-destructive transition-colors duration-100"
                    >
                        <SignOut weight="bold" className="w-4 h-4" />
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <main className="bg-[#F4F4F5]">
                {/* Filter Toolbar */}
                <div className="filter-toolbar bg-white flex-wrap">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        <Funnel weight="bold" className="w-4 h-4" />
                        <span className="hidden sm:inline">Filters</span>
                    </div>

                    {/* Company Filter */}
                    <div className="flex items-center gap-2">
                        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap hidden sm:block">
                            Company
                        </Label>
                        <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                            <SelectTrigger 
                                data-testid="filter-company-select"
                                className="w-36 md:w-48 h-9 border-2"
                            >
                                <SelectValue placeholder="All Skoda" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Skoda Companies</SelectItem>
                                {companies.map((company) => (
                                    <SelectItem key={company.id} value={company.id.toString()}>
                                        {company.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Status Filter */}
                    <div className="flex items-center gap-2">
                        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap hidden sm:block">
                            Status
                        </Label>
                        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                            <SelectTrigger 
                                data-testid="filter-status-select"
                                className="w-36 md:w-48 h-9 border-2"
                            >
                                <SelectValue placeholder="All Statuses" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                {ALLOWED_STATUSES.map((status) => (
                                    <SelectItem key={status.value} value={status.value.toString()}>
                                        {status.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={clearFilters}
                        data-testid="clear-filters-button"
                        className="h-9 border-2"
                    >
                        Clear
                    </Button>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchTickets}
                        data-testid="refresh-tickets-button"
                        className="h-9 border-2 ml-auto"
                    >
                        <ArrowClockwise weight="bold" className="w-4 h-4 md:mr-1" />
                        <span className="hidden md:inline">Refresh</span>
                    </Button>
                </div>

                {/* Tickets Table */}
                <div className="p-2 md:p-6">
                    {error && (
                        <div 
                            data-testid="tickets-error-message"
                            className="mb-4 p-4 border border-destructive bg-destructive/5 text-destructive text-sm"
                        >
                            {error}
                        </div>
                    )}

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-2">
                        {loading ? (
                            <div className="text-center py-12 bg-white border border-border">
                                <span className="font-mono text-sm text-muted-foreground">
                                    LOADING TICKETS...
                                </span>
                            </div>
                        ) : sortedTickets.length === 0 ? (
                            <div className="text-center py-12 bg-white border border-border">
                                <Ticket weight="light" className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                                <span className="text-sm text-muted-foreground">No tickets found</span>
                            </div>
                        ) : (
                            sortedTickets.map((ticket) => (
                                <div 
                                    key={ticket.id}
                                    data-testid={`ticket-card-${ticket.id}`}
                                    className={`border border-border p-4 cursor-pointer ${isResolved(ticket.status) ? '' : 'bg-white active:bg-[#F4F4F5]'}`}
                                    style={isResolved(ticket.status) ? { backgroundColor: '#E5E5E5' } : {}}
                                    onClick={() => handleTicketClick(ticket)}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <span className={`font-mono text-sm font-medium ${isResolved(ticket.status) ? 'text-gray-400' : 'text-[#002FA7]'}`}>#{ticket.id}</span>
                                        <div className="flex gap-1">
                                            {getStatusBadge(ticket.status)}
                                            {getPriorityBadge(ticket.priority)}
                                        </div>
                                    </div>
                                    <p className={`text-sm font-medium mb-2 line-clamp-2 ${isResolved(ticket.status) ? 'text-muted-foreground' : ''}`}>
                                        {ticket.subject}
                                    </p>
                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <Buildings weight="regular" className="w-3 h-3" />
                                            {ticket.company?.name || "-"}
                                        </span>
                                        <span className="font-mono">{formatDateShort(ticket.created_at)}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block bg-white border border-border overflow-x-auto">
                        <Table className="ticket-table">
                            <TableHeader>
                                <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                                    <SortableHeader column="id">ID</SortableHeader>
                                    <SortableHeader column="subject">Subject</SortableHeader>
                                    <SortableHeader column="status">Status</SortableHeader>
                                    <SortableHeader column="priority">Priority</SortableHeader>
                                    <SortableHeader column="type">Type</SortableHeader>
                                    <SortableHeader column="created_at">Created</SortableHeader>
                                    <SortableHeader column="updated_at">Updated</SortableHeader>
                                    <SortableHeader column="delivery">Delivery</SortableHeader>
                                    <SortableHeader column="requester">Requester</SortableHeader>
                                    <SortableHeader column="company">Company</SortableHeader>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} className="text-center py-12">
                                            <span className="font-mono text-sm text-muted-foreground">
                                                LOADING TICKETS...
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ) : sortedTickets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} className="text-center py-12">
                                            <div className="flex flex-col items-center gap-2">
                                                <Ticket weight="light" className="w-12 h-12 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">
                                                    No tickets found
                                                </span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedTickets.map((ticket) => (
                                        <TableRow 
                                            key={ticket.id} 
                                            data-testid={`ticket-row-${ticket.id}`}
                                            className={`cursor-pointer ${isResolved(ticket.status) ? '' : 'hover:bg-[#F4F4F5]/50'}`}
                                            style={isResolved(ticket.status) ? { backgroundColor: '#E5E5E5' } : {}}
                                            onClick={() => handleTicketClick(ticket)}
                                        >
                                            <TableCell className={`py-2 px-4 font-mono text-sm font-medium ${isResolved(ticket.status) ? 'text-gray-400' : 'text-[#002FA7]'}`}>
                                                #{ticket.id}
                                            </TableCell>
                                            <TableCell className={`py-2 px-4 text-sm max-w-xs truncate ${isResolved(ticket.status) ? 'text-gray-400' : ''}`}>
                                                {ticket.subject}
                                            </TableCell>
                                            <TableCell className="py-2 px-4">
                                                {getStatusBadge(ticket.status)}
                                            </TableCell>
                                            <TableCell className="py-2 px-4">
                                                {getPriorityBadge(ticket.priority)}
                                            </TableCell>
                                            <TableCell className={`py-2 px-4 text-xs font-medium ${isResolved(ticket.status) ? 'text-gray-400' : 'text-muted-foreground'}`}>
                                                {ticket.type ? (ticket.type === "Problem" ? "Issue" : ticket.type) : "-"}
                                            </TableCell>
                                            <TableCell className="py-2 px-4 font-mono text-xs text-muted-foreground">
                                                {formatDate(ticket.created_at)}
                                            </TableCell>
                                            <TableCell className="py-2 px-4 font-mono text-xs text-muted-foreground">
                                                {formatDate(ticket.updated_at)}
                                            </TableCell>
                                            <TableCell className="py-2 px-4 font-mono text-xs text-muted-foreground">
                                                {ticket.custom_fields?.cf_deployment_date || "-"}
                                            </TableCell>
                                            <TableCell className="py-2 px-4 text-sm">
                                                {ticket.requester?.email || ticket.requester_id || "-"}
                                            </TableCell>
                                            <TableCell className="py-2 px-4 text-sm">
                                                <div className="flex items-center gap-1">
                                                    <Buildings weight="regular" className="w-4 h-4 text-muted-foreground" />
                                                    {ticket.company?.name || ticket.company_id || "-"}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                        <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                            Page {page} - {sortedTickets.length} tickets
                        </div>
                        {(page > 1 || sortedTickets.length >= 50) && (
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(Math.max(1, page - 1))}
                                    disabled={page === 1}
                                    data-testid="pagination-prev"
                                    className="h-9 border-2"
                                >
                                    <CaretLeft weight="bold" className="w-4 h-4" />
                                    <span className="hidden sm:inline">Prev</span>
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(page + 1)}
                                    disabled={sortedTickets.length === 0}
                                    data-testid="pagination-next"
                                    className="h-9 border-2"
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <CaretRight weight="bold" className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Ticket Detail Modal */}
            <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && closeTicketModal()}>
                <DialogContent className="max-w-4xl max-h-[90vh] border-2 shadow-none p-0 flex flex-col" data-testid="ticket-detail-dialog">
                    <DialogHeader className="px-4 md:px-6 py-4 border-b border-border bg-[#F4F4F5] flex-shrink-0">
                        <div className="flex items-start justify-between gap-2">
                            <DialogTitle className="text-base md:text-lg font-black tracking-tight flex flex-wrap items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                <span className="font-mono text-[#002FA7]">#{selectedTicket?.id}</span>
                                <span className="break-words">{selectedTicket?.subject}</span>
                            </DialogTitle>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-2">
                            {selectedTicket && getStatusBadge(selectedTicket.status)}
                            {selectedTicket && getPriorityBadge(selectedTicket.priority)}
                            <span className="text-xs text-muted-foreground">
                                Created: {formatDate(selectedTicket?.created_at)}
                            </span>
                            
                            {/* Navigation Arrows in Header */}
                            {ticketConversations.length > 1 && (
                                <div className="flex gap-1 ml-auto">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); scrollToPreviousMessage(); }}
                                        className="h-8 w-8 p-0 border-2 bg-white hover:bg-[#002FA7] hover:text-white"
                                        title="Previous message"
                                    >
                                        <ArrowUp weight="bold" className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); scrollToNextMessage(); }}
                                        className="h-8 w-8 p-0 border-2 bg-white hover:bg-[#002FA7] hover:text-white"
                                        title="Next message"
                                    >
                                        <ArrowDown weight="bold" className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </DialogHeader>

                    <div className="flex-1 overflow-auto" ref={scrollAreaRef} style={{ overflowX: 'auto', overflowY: 'auto' }}>
                        <div className="p-4 md:p-6 min-w-0">
                            {/* Ticket Info - removed Type */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-[#F4F4F5] border border-border">
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Requester</span>
                                    <p className="text-sm mt-1">{selectedTicket?.requester?.email || selectedTicket?.requester_id || "-"}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Company</span>
                                    <p className="text-sm mt-1">{selectedTicket?.company?.name || "-"}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Last Updated</span>
                                    <p className="text-sm mt-1">{formatDate(selectedTicket?.updated_at)}</p>
                                </div>
                            </div>

                            {/* Original Description */}
                            {selectedTicket?.description_text && (
                                <div 
                                    className="mb-6"
                                    ref={el => conversationRefs.current[0] = el}
                                    id="message-0"
                                >
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                                        <ChatCircle weight="bold" className="w-4 h-4" />
                                        Original Message
                                    </h3>
                                    <div className={`p-4 border-2 transition-all bg-white overflow-x-auto ${currentMessageIndex === 0 ? 'border-[#002FA7] ring-2 ring-[#002FA7]/20' : 'border-border'}`}>
                                        <pre className="text-sm whitespace-pre-wrap break-words font-sans" style={{ overflowWrap: 'break-word' }}>{formatConversationText(selectedTicket.description_text)}</pre>
                                    </div>
                                </div>
                            )}

                            {/* Conversation Thread */}
                            <div>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
                                    <ChatCircle weight="bold" className="w-4 h-4" />
                                    Conversation Thread ({ticketConversations.length + (selectedTicket?.description_text ? 1 : 0)})
                                </h3>

                                {loadingConversations ? (
                                    <div className="text-center py-8">
                                        <span className="font-mono text-sm text-muted-foreground">
                                            LOADING CONVERSATIONS...
                                        </span>
                                    </div>
                                ) : ticketConversations.length === 0 ? (
                                    <div className="text-center py-8 text-sm text-muted-foreground">
                                        No conversations yet
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {ticketConversations.map((conv, index) => (
                                            <div 
                                                key={conv.id || index}
                                                ref={el => conversationRefs.current[index + 1] = el}
                                                id={`message-${index + 1}`}
                                                className={`p-4 border-2 transition-all overflow-x-auto ${conv.incoming ? 'bg-white' : 'bg-[#002FA7]/5'} ${currentMessageIndex === index + 1 ? 'border-[#002FA7] ring-2 ring-[#002FA7]/20' : 'border-border'}`}
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <User weight="bold" className="w-4 h-4 text-muted-foreground" />
                                                        <span className="text-sm font-medium">
                                                            {conv.from_email || "Agent"}
                                                        </span>
                                                        {!conv.incoming && (
                                                            <span className="status-badge status-open text-[10px]">AGENT</span>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-muted-foreground font-mono">
                                                        {formatDate(conv.created_at)}
                                                    </span>
                                                </div>
                                                <div className="text-sm whitespace-pre-wrap break-words" style={{ overflowWrap: 'break-word' }}>
                                                    {conv.body ? stripHtml(conv.body) : formatConversationText(conv.body_text)}
                                                </div>
                                                {conv.attachments && conv.attachments.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-border">
                                                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                                            <PaperclipHorizontal weight="bold" className="w-3 h-3" />
                                                            Attachments ({conv.attachments.length})
                                                        </span>
                                                        <div className="mt-2 flex flex-wrap gap-2">
                                                            {conv.attachments.map((att, i) => (
                                                                <a 
                                                                    key={i}
                                                                    href={att.attachment_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-xs text-[#002FA7] hover:underline"
                                                                >
                                                                    {att.name}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DashboardPage;
